import { useTranslation } from 'react-i18next'
import ExpandableList from './ExpandableList'
import { formatDate } from './cycleStatus'
import {
  fmtNum, humanizeCode, markerLabel, sectionHasContent, showNarrative, sleepExperiment, supplementsView,
  valueWithUnit, wellnessLines,
} from './healthReportFormat'

const tr = (t, key, code) => t(key, { defaultValue: humanizeCode(code) })

function List({ items, render, empty }) {
  if (!items?.length) return empty ? <p className="text-sm text-gray-500">{empty}</p> : null
  return <ul className="space-y-1 text-sm text-gray-700">{items.map((item, i) => <li key={i}>{render(item)}</li>)}</ul>
}

function SubHeading({ children }) {
  return <h5 className="text-xs font-semibold uppercase text-gray-600">{children}</h5>
}

function VerdictFacts({ facts }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="space-y-2 text-sm">
      <p className="text-gray-800">
        {t('cycleReports.health.verdictCounts', {
          abnormal: facts.abnormal_count ?? 0, total: facts.results_count ?? 0, real: facts.real_count ?? 0,
          artifact: facts.artifact_count ?? 0, adaptation: facts.adaptation_count ?? 0,
        })}
      </p>
      {facts.only_artifacts && <p className="text-green-700">{t('cycleReports.health.onlyArtifacts')}</p>}
      <List items={facts.real_findings} render={(f) => (
        <span className="flex flex-wrap justify-between gap-2">
          <span><span className="font-medium text-gray-900">{f.marker_name}</span> {valueWithUnit(f.value, f.unit, i18n.language)}</span>
          <span className={`text-xs ${f.urgency === 'urgent' ? 'text-red-700' : 'text-gray-600'}`}>{tr(t, `cycleReports.urgency.${f.urgency}`, f.urgency)}</span>
        </span>
      )} />
    </div>
  )
}

function MarkersFacts({ facts }) {
  const { t, i18n } = useTranslation()
  return (
    <List items={facts.markers} empty={t('cycleReports.health.noAbnormal')} render={(m) => (
      <div className="border border-gray-200 rounded-md p-2" data-testid="marker-by-cause">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{markerLabel({}, m.marker_code, m.marker_name)}</span>
          <span>{[m.value_operator, valueWithUnit(m.value, m.unit, i18n.language)].filter(Boolean).join(' ')}</span>
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">{tr(t, `cycleReports.classification.${m.classification}`, m.classification)}</span>
          <span className="text-xs text-gray-600">{tr(t, `cycleReports.urgency.${m.urgency}`, m.urgency)}</span>
        </div>
        {m.triggers?.length > 0 && (
          <p className="text-xs text-gray-600 mt-1">
            {t('cycleReports.health.trigger')}: {m.triggers.map((x) => x.trigger_detail || humanizeCode(x.rule_code)).join(', ')}
          </p>
        )}
        {m.trend?.length > 1 && (
          <p className="text-xs text-gray-500 mt-1">
            {t('cycleReports.health.trend')}: {m.trend.map((p) => `${formatDate(p.date, i18n.language)} ${fmtNum(p.value, i18n.language, 2) || '—'}`).join(' → ')}
          </p>
        )}
      </div>
    )} />
  )
}

function SupplementFacts({ facts }) {
  const { t, i18n } = useTranslation()
  if (facts.none_recorded) return <p className="text-sm text-gray-600">{t('cycleReports.health.noSupplements')}</p>
  const { flagged, quiet } = supplementsView(facts)
  return (
    <div className="space-y-3">
      {flagged.map((group) => (
        <div key={group.recommendation}>
          <SubHeading>{tr(t, `cycleReports.supplementRecommendation.${group.recommendation}`, group.recommendation)}</SubHeading>
          <List items={group.supplements} render={(s) => (
            <span>
              <span className="font-medium text-gray-900">{s.name}</span>
              {s.evidence && s.evidence !== 'not_classified' && ` · ${tr(t, `cycleReports.evidence.${s.evidence}`, s.evidence)}`}
              {(s.above_limit ?? []).map((o, i) => (
                <span key={o.ingredient_code ?? i} className="block text-xs text-amber-700">
                  {t('cycleReports.health.aboveLimit', {
                    ingredient: tr(t, `cycleReports.ingredient.${o.ingredient_code}`, o.ingredient_code),
                    dose: valueWithUnit(o.dose_in_limit_unit, o.limit_unit, i18n.language),
                    limit: valueWithUnit(o.upper_limit_per_day, o.limit_unit, i18n.language),
                    percent: fmtNum(o.percent_of_limit, i18n.language, 0), authority: o.authority ?? '',
                  })}
                </span>
              ))}
            </span>
          )} />
        </div>
      ))}
      {quiet.length > 0 && (
        <details className="text-sm" data-testid="supplements-no-finding">
          <summary className="cursor-pointer text-gray-600">{t('cycleReports.health.supplementsNoFinding', { count: quiet.length })}</summary>
          <p className="mt-1 text-xs text-gray-500">{quiet.join(', ')}</p>
        </details>
      )}
    </div>
  )
}

function TrainingFacts({ facts }) {
  const { t, i18n } = useTranslation()
  const pct = (v) => (v == null ? '' : ` ${v > 0 ? '+' : ''}${fmtNum(v, i18n.language)} %`)
  const line = (e) => (
    <span>
      {e.exercise_name}<span className="text-gray-500">{pct(e.change_pct)}</span>
      {e.plateau && (
        <span className="block text-xs text-gray-500">
          {t('cycleReports.health.plateau', {
            weight: fmtNum(e.plateau.weight_kg, i18n.language), weeks: e.plateau.weeks ?? '—',
            suggestion: tr(t, `cycleReports.health.plateauSuggestion.${e.plateau.suggestion}`, e.plateau.suggestion),
          })}
        </span>
      )}
    </span>
  )
  return (
    <div className="grid gap-3 sm:grid-cols-2 text-sm">
      <div><SubHeading>{t('cycleReports.health.change')}</SubHeading><ExpandableList items={facts.change} render={line} /></div>
      <div><SubHeading>{t('cycleReports.health.kept')}</SubHeading><ExpandableList items={facts.kept} render={line} /></div>
    </div>
  )
}

function WellnessFacts({ facts }) {
  const { t, i18n } = useTranslation()
  const n = (v) => fmtNum(v, i18n.language)
  const experiment = sleepExperiment(facts.sleep_constancy)
  return (
    <ul className="space-y-1 text-sm text-gray-700">
      {wellnessLines(facts).map((m) => {
        const label = t(`cycleReports.health.metric.${m.key}`)
        const unit = m.unit ?? ''
        let text
        if (m.mean != null && m.change != null) {
          text = t('cycleReports.health.metricMeanChange', {
            label, mean: n(m.mean), unit, change: `${m.change > 0 ? '+' : ''}${n(m.change)}`,
            from: m.from ?? '', to: m.to ?? '',
          })
        } else if (m.mean != null) {
          text = t('cycleReports.health.metricMean', { label, mean: n(m.mean), unit })
        } else {
          text = t('cycleReports.health.metricRange', { label, first: n(m.first), last: n(m.last), unit })
        }
        return <li key={m.key} data-testid={`wellness-${m.key}`}>{text}</li>
      })}
      {facts.recovery_status && (
        <li>{t('cycleReports.health.recovery')}: {tr(t, `cycleReports.health.recoveryStatus.${facts.recovery_status}`, facts.recovery_status)}</li>
      )}
      {facts.poor_recovery_nights > 0 && <li>{t('cycleReports.health.poorRecoveryNights', { count: facts.poor_recovery_nights })}</li>}
      {facts.sleep_constancy?.applies && (
        <li>{t('cycleReports.health.sleepConstancy', { range: n(facts.sleep_constancy.range_minutes), threshold: n(facts.sleep_constancy.threshold_minutes) })}</li>
      )}
      {facts.sleep_constancy?.applies && experiment && (
        <li>{t('cycleReports.health.sleepExperiment', { days: experiment.days, baseline: n(experiment.baseline), target: n(experiment.target) })}</li>
      )}
    </ul>
  )
}

function PreparationFacts({ facts }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2 text-sm">
      {facts.tests?.length > 0 && (
        <>
          <SubHeading>{t('cycleReports.health.testsToRequest')}</SubHeading>
          <ExpandableList items={facts.tests} limit={6} render={(x) => (
            <span>{x.test}{x.reason && <span className="text-gray-500"> — {tr(t, `cycleReports.health.testReason.${x.reason}`, x.reason)}</span>}</span>
          )} />
        </>
      )}
      {facts.protocol?.length > 0 && (
        <>
          <SubHeading>{t('cycleReports.health.protocol')}</SubHeading>
          <List items={facts.protocol} render={(s) => (
            <span><span className="font-medium">{t('cycleReports.health.daysBeforeDraw', { count: s.days_before_draw })}</span>: {s.text}</span>
          )} />
        </>
      )}
    </div>
  )
}

function StrengthsFacts({ facts }) {
  const { t, i18n } = useTranslation()
  const groups = [
    ['stable', [...(facts.improved_markers ?? []).map((m) => t('cycleReports.health.improvedMarker', {
      name: m.marker_name, from: fmtNum(m.previous_value, i18n.language, 2), value: valueWithUnit(m.value, m.unit, i18n.language),
    })), ...(facts.stable_good_markers ?? []).map((m) => t('cycleReports.health.stableMarker', {
      name: m.marker_name, value: valueWithUnit(m.value, m.unit, i18n.language), tests: m.tests,
    }))]],
    ['training', (facts.progressing_exercises ?? []).map((e) => `${e.exercise_name}${e.change_pct != null ? ` +${fmtNum(e.change_pct, i18n.language)} %` : ''}`)],
  ].filter(([, items]) => items.length > 0)
  return (
    <div className="grid gap-3 sm:grid-cols-2 text-sm">
      {groups.map(([key, items]) => (
        <div key={key} data-testid={`strengths-${key}`}>
          <SubHeading>{t(`cycleReports.health.strengthGroup.${key}`)}</SubHeading>
          <ExpandableList items={items} render={(x) => x} />
        </div>
      ))}
    </div>
  )
}

function SystemViewFacts({ facts }) {
  const { t } = useTranslation()
  const r = facts.readiness
  return (
    <div className="space-y-1 text-sm text-gray-700">
      <List items={facts.loops} render={(l) => (
        <span>{tr(t, `cycleReports.health.loop.${l.code ?? l.key}`, l.code ?? l.key)}{l.break_point ? ` · ${l.break_point}` : ''}</span>
      )} />
      {r?.weeks && (
        <p>{t('cycleReports.health.readiness', {
          nutrition: r.weeks.nutrition ?? 0, sleep: r.weeks.sleep ?? 0, training: r.weeks.training ?? 0, overlap: r.overlapping_weeks ?? 0,
        })}</p>
      )}
    </div>
  )
}

function OtherFacts({ section, onOpenNutrition, names, recommendationTitles }) {
  const { t, i18n } = useTranslation()
  const { id, facts } = section
  if (id === 'questions') {
    return <List items={facts.answers} render={(a) => (
      <span><span className="font-medium">{a.question}</span>{a.markers?.length > 0 && ` — ${a.markers.map((m) => `${markerLabel(names, m.marker_code, m.marker_name)} ${valueWithUnit(m.value, m.unit, i18n.language)}`).join('; ')}`}</span>
    )} />
  }
  if (id === 'system_view') return <SystemViewFacts facts={facts} />
  if (id === 'nutrition_findings') {
    return (
      <div className="space-y-1">
        <List items={facts.lines} render={(l) => `${humanizeCode(l.from)} ↔ ${humanizeCode(l.to)}${l.strength ? ` (${tr(t, `cycleReports.health.strength.${l.strength}`, l.strength)})` : ''}`} />
        {onOpenNutrition && (
          <button type="button" className="text-sm text-primary-600 underline" onClick={onOpenNutrition}>
            {t('cycleReports.health.openNutritionReport')}
          </button>
        )}
      </div>
    )
  }
  if (id === 'conflicts') {
    return <List items={facts.conflicts} render={(c) => (
      <span>
        {markerLabel(names, c.marker_code, c.marker_name)}: {t('cycleReports.health.conflictDecision', { decision: recommendationTitles[c.decision] ?? humanizeCode(c.decision) })}
        {c.condition && ` — ${c.condition}`}
      </span>
    )} />
  }
  return null
}

const FACT_RENDERERS = {
  verdict: VerdictFacts, markers_by_cause: MarkersFacts, improve_supplements: SupplementFacts,
  improve_training: TrainingFacts, improve_wellness: WellnessFacts, preparation: PreparationFacts, strengths: StrengthsFacts,
}

// VH-228: one report section — title, the structured facts, and the prose only when it
// adds to them; a section that could not be prepared is shown with its reason.
export function HealthReportSection({ section, onOpenNutrition = null, recommendationCount = 0, names = {}, recommendationTitles = {}, children = null }) {
  const { t } = useTranslation()
  const notAvailable = section.status === 'not_available'
  const Facts = FACT_RENDERERS[section.id]
  const facts = section.facts ?? {}
  const hasContent = sectionHasContent(section, recommendationCount)
  return (
    <article id={`health-section-${section.id}`} className="border border-gray-200 rounded-lg p-3 space-y-2 scroll-mt-4" data-testid={`health-section-${section.id}`}>
      <h4 className="text-base font-semibold text-gray-900">{tr(t, `cycleReports.health.sections.${section.id}`, section.id)}</h4>
      {notAvailable ? (
        <p className="text-sm text-gray-600">{t('cycleReports.notAvailablePlain')}</p>
      ) : (
        <>
          {showNarrative(section, hasContent) && <p className="text-sm text-gray-800 whitespace-pre-line">{section.narrative}</p>}
          {section.id !== 'levers' && hasContent && (Facts
            ? <Facts facts={facts} />
            : <OtherFacts section={{ ...section, facts }} onOpenNutrition={onOpenNutrition} names={names} recommendationTitles={recommendationTitles} />)}
          {section.id === 'nutrition_findings' && !hasContent && onOpenNutrition && (
            <button type="button" className="text-sm text-primary-600 underline" onClick={onOpenNutrition}>
              {t('cycleReports.health.openNutritionReport')}
            </button>
          )}
          {children}
        </>
      )}
    </article>
  )
}

export default HealthReportSection
